<template>
  <ModalDialog
    header-text="Payment Details"
    contentClass="payment-modal-content"
    :canBeClosed="!paymentInProgress"
    :confirm-on-close="true"
    close-confirmation-message="Are you sure you want to close the payment modal? Your current payment selections will be lost."
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div v-if="paymentInProgress" class="payment-processing">
        <Loader class="payment-loader" size="large" type="pulsing" />
      </div>

      <div v-if="showSensePass" class="sense-pass__container">
        <div class="sense-pass__column">
          <div class="sense-pass__header">
            <span class="sense-pass__header-back" @click="showSensePass = false">
              <font-awesome-icon icon="fa-solid fa-arrow-left" />
              <span class="sense-pass__header-back-text">Back to Payment Amounts</span>
            </span>
          </div>
          <div class="form-control">
            <label>Company Id</label>
            <input type="text" :value="main.companyLogin" readonly disabled />
          </div>
          <div class="form-control">
            <label>Company Name</label>
            <input type="text" :value="main.companySummary!.Name" readonly disabled />
          </div>
          <div class="form-control">
            <label>Email Address</label>
            <input type="text" :value="main.companySummary!.WebContactEmail" readonly disabled />
          </div>
          <div class="form-control">
            <label>Invoice Number(s)</label>
            <input type="text" :value="transactions.map((t) => t.DocumentNumber).join(', ')" readonly disabled />
          </div>
          <div class="form-control">
            <label>Payment Amount</label>
            <input type="text" :value="CurrencyFormatter.format(totalAmount)" readonly disabled />
          </div>
          <div class="form-control">
            <label>{{ serviceFeeLabel }}</label>
            <input type="text" :value="CurrencyFormatter.format(serviceFeeAmount)" readonly disabled />
          </div>
          <div class="form-control">
            <label>Total Payment</label>
            <input type="text" :value="CurrencyFormatter.format(totalPaymentAmount)" readonly disabled />
          </div>
          <div class="form-control">
            <label>Comments</label>
            <textarea rows="4" cols="50" v-model="creditCardComments"></textarea>
          </div>
        </div>
        <div class="sense-pass__column sensepass">
          <PaymentOptions
            v-if="paymentOptions.length"
            :payment-options="paymentOptions"
            :selected-payment-option-key="selectedPaymentOptionKey"
            @select="selectPaymentOption"
            @deleted="
              (id) => {
                savedSensePassPaymentMethods = savedSensePassPaymentMethods.filter((m) => m.Id !== id);
              }
            "
          />
          <div
            v-if="selectedPaymentOption && selectedPaymentOption.submitMode !== 'sensepass-new'"
            class="sense-pass__saved-card"
          >
            <div class="sense-pass__saved-card-label">{{ selectedPaymentOptionSummaryLabel }}</div>
            <div class="sense-pass__saved-card-value">{{ selectedPaymentOption.label }}</div>
            <Button theme-color="primary" type="button" @click="submitSelectedPaymentOptionAsync"> Pay Now </Button>
          </div>
          <SensePass
            v-else-if="selectedPaymentOption?.submitMode === 'sensepass-new'"
            :key="selectedPaymentOptionKey"
            :open="showSensePass"
            :config="sensePassConfig"
            :amount="totalAmountWithFeeInCents"
            @payCompleted="completeSensePassPaymentAsync"
            @paymentSelected="onSensePassPaymentSelected"
            @error="onSensePassError"
          >
            <div class="save-payment-method-wrapper">
              <input
                id="savePaymentMethod"
                class="save-payment-method-checkbox"
                type="checkbox"
                v-model="savePaymentMethod"
              />
              <label for="savePaymentMethod">Save payment method for future use</label>
            </div>
          </SensePass>
          <div v-else class="payment-options__empty">Payment options are not available at this time.</div>
          <div v-if="main.isEmulator && !isAchPayment" class="include-fee-wrapper">
            <input id="includeFee" class="include-fee-checkbox" type="checkbox" v-model="includeFee" /><label
              for="includeFee"
              >Include service fee</label
            >
          </div>
          <div class="fee-text-block">
            {{ serviceFeeText }}
          </div>
        </div>
      </div>

      <div v-else class="flex-container">
        <Grid :data-items="transactions" :columns="columns" :scrollable="'none'">
          <template v-slot:DeleteCell="{ props: { dataItem } }">
            <td class="k-table-td">
              <font-awesome-icon class="open-delete" @click.prevent="deleteTransactionClicked(dataItem)" icon="times" />
            </td>
          </template>
          <template v-slot:PaymentCell="{ props: { dataItem } }">
            <td class="k-table-td">
              <NumericTextBox
                :default-value="dataItem.TransactionBalance"
                :min="getMinTransactionAmount(dataItem)"
                :max="dataItem.TransactionBalance"
                v-model="paymentAmounts[transactions.indexOf(dataItem)]"
                :decimals="2"
                format="c2"
                :spinners="false"
                :step="0.01"
              />
            </td>
          </template>
          <template v-slot:BalanceFooter>
            <span> {{ totalBalance ? CurrencyFormatter.format(totalBalance) : "$0.00" }}</span>
          </template>
          <template v-slot:PaymentFooter>
            <span> {{ totalAmount ? CurrencyFormatter.format(totalAmount) : "$0.00" }}</span>
          </template>
        </Grid>
        <div class="actions-container">
          <Button theme-color="dark" type="button" @click="payClicked" data-cy="pay-card-button">
            Continue to Payment Method
          </Button>
        </div>
        <div class="payment-text-block">
          Please be advised that by continuing, you agree to the Privacy Policies<br />
          of the
          <a href="https://www.oukosher.org/privacy-policy/" target="_blank">OU</a>
          and
          <a href="https://www.cardknox.com/privacy-policy/" target="_blank">CardKnox</a>
          &
          <a href="https://sensepass.com/customer-termsn-and-conditions/" target="_blank">Sensepass</a>.
        </div>
        <div class="payment-text-block">
          Payment receipt will be sent to email address
          <span>{{ userEmail }}</span>
        </div>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { FinancialTransaction } from "@/Domain/FinancialTransaction";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { NumericTextBox } from "@progress/kendo-vue-inputs";
import { Button } from "@progress/kendo-vue-buttons";
import { computed, onMounted, ref, watch } from "vue";
import { CurrencyFormatter } from "@/shared/helpers/formatHelper";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { SensePassSavedPaymentMethod } from "@/Domain/SensePassSavedPaymentMethod";
import { EnumSensePassPaymentMethod } from "@/Domain/EnumSensePassPaymentMethod";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { Loader } from "@progress/kendo-vue-indicators";
import SensePass, { type SensePassConfig, type SensePassPayCompletedPayload } from "@/shared/ui/payments/SensePass.vue";
import { getEnvValue, ENV_KEYS } from "@/shared/helpers/envHelper";
import PaymentOptions, { type PaymentOption } from "./PaymentOptions.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  transactions: FinancialTransaction[];
}>();

const emit = defineEmits<{
  (e: "closeModal", paymentCompleted: boolean): void;
  (e: "deleteTransaction", transaction: FinancialTransaction): void;
}>();

const emitCloseModal = function (paymentCompleted: boolean = false) {
  emit("closeModal", paymentCompleted);
};

const closeModal = function (closed: boolean) {
  if (!closed) {
    if (paymentInProgress.value) {
      notificationStore.notify({
        message: "Please wait while payment is being processed",
        type: NotificationTypeEnum.info,
        duration: 5000,
      });
    }
    return;
  }

  emitCloseModal(false);
};

const paymentAmounts = ref<number[]>([]);
const paymentInProgress = ref<boolean>(false);
const userEmail = ref<string>("");
const savedSensePassPaymentMethods = ref<SensePassSavedPaymentMethod[]>([]);
const selectedPaymentOptionKey = ref<string>("");
const sensePassIframeIsAch = ref<boolean>(false);
const showSensePass = ref<boolean>(false);
const creditCardComments = ref<string>("");
const includeFee = ref<boolean>(true);
const creditCardServiceFee = ref<number>(0);
const savePaymentMethod = ref<boolean>(true);

const getSavedPaymentMethodLabel = function (paymentMethod: SensePassSavedPaymentMethod) {
  if (paymentMethod.PaymentMethodType === EnumSensePassPaymentMethod.Ach)
    return (paymentMethod.CardMask ?? paymentMethod.LastFourDigits)
      ? `Bank account ${paymentMethod.CardMask ?? paymentMethod.LastFourDigits}`
      : "Bank account";

  const cardType = "Credit card";
  const expiration = paymentMethod.Expiration
    ? ` expiring ${
        paymentMethod.Expiration.length === 4
          ? paymentMethod.Expiration.substring(0, 2) + "/" + paymentMethod.Expiration.substring(2)
          : paymentMethod.Expiration
      }`
    : "";

  return paymentMethod.LastFourDigits
    ? `${cardType} ${paymentMethod.CardMask ?? paymentMethod.LastFourDigits}${expiration}`
    : `${cardType}${expiration}`;
};

const sensePassConfig = computed(() => {
  return {
    mode: getEnvValue(ENV_KEYS.SensepassMode) ?? "sandbox",
    debug: getEnvValue(ENV_KEYS.SensepassDebug) == "true",
    visualizer: false,
    clientId: getEnvValue(ENV_KEYS.SensepassClientId) ?? "",
    theme: {
      material: "https://pay.sandbox.sensepass.com/publicAssets/themes/custom-sample-material.theme.css",
      sensepassEcommerce: "https://pay.sandbox.sensepass.com/publicAssets/themes/custom-sample-ecommerce.theme.css",
      config: {},
    },
    methodType: "tokenize",
  } as SensePassConfig;
});

const paymentOptions = computed<PaymentOption[]>(() => {
  const options = savedSensePassPaymentMethods.value.map((paymentMethod) => {
    return {
      key: `saved-${paymentMethod.Id}`,
      type: paymentMethod.PaymentMethodType,
      label: getSavedPaymentMethodLabel(paymentMethod),
      description:
        paymentMethod.PaymentMethodType === EnumSensePassPaymentMethod.Ach ? "Saved bank account" : "Saved card",
      submitMode: "sensepass-saved",
      savedPaymentMethodId: paymentMethod.Id,
    } as PaymentOption;
  });

  if (sensePassConfig.value.clientId) {
    options.push({
      key: "new-payment-method",
      type: EnumSensePassPaymentMethod.CreditCard,
      label: options.length ? "Use a different payment method" : "Add a payment method",
      submitMode: "sensepass-new",
    } as PaymentOption);
  }

  return options;
});

const selectedPaymentOption = computed(() => {
  return paymentOptions.value.find((option) => option.key === selectedPaymentOptionKey.value) ?? null;
});

const isAchPayment = computed(() => {
  return selectedPaymentOption.value?.type === EnumSensePassPaymentMethod.Ach || sensePassIframeIsAch.value;
});

const selectedPaymentOptionSummaryLabel = computed(() => {
  if (selectedPaymentOption.value?.type === EnumSensePassPaymentMethod.Ach) return "Pay with Bank Account";
  return "Pay with Credit Card";
});

const serviceFeeAmount = computed(() => {
  if (isAchPayment.value) return 0;
  return includeFee.value ? totalAmount.value * (creditCardServiceFee.value / 100) : 0;
});

const totalPaymentAmount = computed(() => {
  return totalAmount.value + serviceFeeAmount.value;
});

const serviceFeeLabel = computed(() => {
  if (isAchPayment.value) return "Service Fee";
  return `Service Fee (${creditCardServiceFee.value}%${includeFee.value ? "" : " Not Included"})`;
});

const serviceFeeText = computed(() => {
  if (isAchPayment.value) return "ACH payments do not incur a service fee.";
  return `We impose a surcharge of ${creditCardServiceFee.value}% on credit cards that is not greater than our cost of acceptance`;
});

const columns: GridColumnProps[] = [
  {
    title: "",
    field: "",
    cell: "DeleteCell",
    width: "50px",
  },
  {
    title: "Transaction ID",
    field: "DocumentNumber",
  },
  {
    title: "Date",
    field: "DocumentDate",
    type: "date",
    format: "{0:MM/dd/yyyy}",
  },
  {
    title: "Invoice Type",
    field: "InvoiceType",
  },
  {
    title: "Transaction Type",
    field: "DocumentType",
  },
  {
    title: "Transaction Balance",
    field: "TransactionBalance",
    type: "number",
    format: "{0:c}",
    footerCell: "BalanceFooter",
  },
  {
    title: "Amount to Pay",
    field: "TransactionBalance",
    cell: "PaymentCell",
    width: "160px",
    footerCell: "PaymentFooter",
  },
];

const totalBalance = computed(() => {
  return props.transactions.reduce((acc, curr) => {
    return acc + curr.TransactionBalance;
  }, 0);
});

const totalAmount = computed(() => {
  return paymentAmounts.value.reduce((acc, curr) => {
    return acc + curr;
  }, 0);
});

const totalAmountWithFeeInCents = computed(() => {
  return Math.round(totalPaymentAmount.value * 100);
});

const getUserEmailAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    userName: main.userLogin,
    userIsOnline: true,
  };

  return await CacheService.getResponseAsync(createCacheKey(requestParams, "UserEmail"), () =>
    api.get("GetUserEmail", requestParams)
  );
};

const updateUserEmailAsync = async function () {
  await handleResponsePromiseAsync(
    getUserEmailAsync(),
    (data) => {
      userEmail.value = data;
    },
    "PaymentModal.vue",
    "updateUserEmailAsync"
  );
};

const updateSensePassSavedPaymentMethodsAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  };

  await handleResponsePromiseAsync(
    api.get("GetSensePassSavedPaymentMethods", requestParams),
    (data) => {
      savedSensePassPaymentMethods.value = data ?? [];
      selectedPaymentOptionKey.value = paymentOptions.value[0]?.key ?? "";
    },
    "PaymentModal.vue",
    "updateSensePassSavedPaymentMethodsAsync",
    () => {
      savedSensePassPaymentMethods.value = [];
    }
  );
};

const selectPaymentOption = function (paymentOptionKey: string) {
  selectedPaymentOptionKey.value = paymentOptionKey;
  sensePassIframeIsAch.value = false;
};

const onSensePassPaymentSelected = function (payload: any) {
  // Detect ACH/bank payment method selected inside the SensePass iframe
  sensePassIframeIsAch.value = payload?.type == 4 || payload?.name?.includes("Pay by Bank");
};

const completeSensePassPaymentAsync = async function (payload: SensePassPayCompletedPayload) {
  const transactionNumber = payload.TransactionNumber;

  if (!transactionNumber) {
    notificationStore.notify({
      message: "Payment Failed. Please try again later.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    console.error(
      "PaymentModal.vue: completeSensePassPaymentAsync():, TransactionNumber is missing, payload =",
      payload
    );
    emitCloseModal();
    return;
  }

  await submitSensePassChargeAsync(transactionNumber);
};

const submitSelectedPaymentOptionAsync = async function () {
  if (!selectedPaymentOption.value) {
    notificationStore.notify({
      message: "Payment Failed. Please choose a payment method and try again.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    return;
  }

  if (!selectedPaymentOption.value.savedPaymentMethodId) {
    notificationStore.notify({
      message: "Payment Failed. Please choose a payment method and try again.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    return;
  }

  await submitSensePassChargeAsync(undefined, selectedPaymentOption.value.savedPaymentMethodId);
};

const submitSensePassChargeAsync = async function (transactionNumber?: string, savedPaymentMethodId?: number) {
  if (paymentInProgress.value) return;

  paymentInProgress.value = true;

  const chargePromise = api.post("SensePassCharge", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    transactionNumber: transactionNumber,
    savedPaymentMethodId: savedPaymentMethodId,
    amount: Math.round(totalAmount.value * 100),
    transactionIds: props.transactions.map((t) => t.DocumentNumber).join(";"),
    paymentAmounts: paymentAmounts.value.join(";"),
    comments: creditCardComments.value,
    includeFee: !isAchPayment.value && includeFee.value,
    savePaymentMethod: savePaymentMethod.value,
  });

  await handleResponsePromiseAsync(
    chargePromise,
    (data) => {
      if (data) {
        notificationStore.notify({
          message: "Payment Successful",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      } else {
        notificationStore.notify({
          message: isAchPayment.value
            ? "Payment Failed. Please try using a different bank account or try again later."
            : "Payment Failed. Please try using a different card or try again later.",
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
      }
      emitCloseModal(data);
    },
    "PaymentModal.vue",
    "CompleteSensePassPaymentAsync",
    () => {
      notificationStore.notify({
        message: isAchPayment.value
          ? "Payment Failed. Please try using a different bank account or try again later."
          : "Payment Failed. Please try using a different card or try again later.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
      emitCloseModal();
    }
  );

  paymentInProgress.value = false;
};

const payClicked = async function () {
  if (totalAmount.value <= 0) {
    notificationStore.notify({
      message: "Payment amount must be greater than $0.00.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    return;
  }

  if (!paymentOptions.value.length) {
    notificationStore.notify({
      message: "Payment options are not available at this time. Please try again later.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    return;
  }

  showSensePass.value = true;
};

const onSensePassError = function (payload: any) {
  paymentInProgress.value = false;
  console.debug("PaymentModal.vue: onSensePassError(): payload =", payload);
  notificationStore.notify({
    message: "Payment Failed",
    type: NotificationTypeEnum.error,
    duration: 5000,
  });
};

const resetModal = function (transactions?: FinancialTransaction[]) {
  if (transactions) paymentAmounts.value = transactions.map((t) => t.TransactionBalance);
  else paymentAmounts.value = props.transactions.map((t) => t.TransactionBalance);
  paymentInProgress.value = false;
  showSensePass.value = false;
  savedSensePassPaymentMethods.value = [];
  selectedPaymentOptionKey.value = "";
  savePaymentMethod.value = true;
  updateSensePassSavedPaymentMethodsAsync();
};

const getServiceFeeAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  };

  const promise = CacheService.getResponseAsync(createCacheKey(requestParams, "GetCcServiceFee"), () =>
    api.get("GetCcServiceFee", requestParams)
  );

  const errorNotification = {
    message: "Failed to retrieve service fee. Please try again later.",
    type: NotificationTypeEnum.error,
    duration: 5000,
  };

  return handleResponsePromiseAsync(
    promise,
    (data) => {
      // Validate that service fee is not negative
      if (data < 0) {
        console.error("PaymentModal.vue: getServiceFeeAsync(): Service fee is negative, data:", data);
        notificationStore.notify(errorNotification);
        return;
      }
      creditCardServiceFee.value = data;
    },
    "PaymentModal.vue",
    "getServiceFeeAsync",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
};

const deleteTransactionClicked = function (transaction: FinancialTransaction) {
  // Don't allow deleting the last transaction
  if (props.transactions.length == 1) {
    notificationStore.notify({
      message: "At least one transaction must be selected",
      type: NotificationTypeEnum.info,
      duration: 5000,
    });
    return;
  }
  // Remove payment amounts for deleted transaction
  const index = props.transactions.indexOf(transaction);
  if (index > -1) paymentAmounts.value.splice(index, 1);

  emit("deleteTransaction", transaction);
};

const getMinTransactionAmount = function (transaction: FinancialTransaction): number {
  if (transaction.TransactionBalance >= 0.5) return 0.5;
  return 0.01;
};

watch(
  paymentOptions,
  (options) => {
    if (options.some((option) => option.key === selectedPaymentOptionKey.value)) return;
    selectedPaymentOptionKey.value = options[0]?.key ?? "";
  },
  { immediate: true }
);

watch(
  () => props.transactions,
  (newVal) => {
    resetModal(newVal);
  }
);

onMounted(() => {
  resetModal();
  getServiceFeeAsync();
  updateUserEmailAsync();
});
</script>

<style lang="scss">
.payment-modal-content {
  @include media-breakpoint-up(sm) {
    width: 80vw;
    max-width: 768px;
  }
}
</style>

<style scoped lang="scss">
.payment-processing {
  position: absolute;
  z-index: 1001;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);

  .payment-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    background: white;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}

.flex-container {
  flex-direction: column;

  .actions-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-top: 20px;
    gap: 8px;
    flex-wrap: wrap;
  }

  .payment-text-block {
    margin-top: 40px;
    text-align: center;
    font-size: 12px;
  }
}

.header-close {
  font-size: 16px;
  padding: 10px;
  cursor: pointer;
}

.terms {
  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 10px;
    gap: 8px;
  }
  p {
    font-family: var(--bs-font-sans-serif);
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    margin-bottom: 12px;
  }
  &__input {
    margin-right: 8px;
  }
}

.fee-text-block {
  margin: auto;
  margin-top: 20px;
  text-align: center;
  font-size: 12px;
  border: 1px solid black;
  padding: 12px;
  background: white;
}

.include-fee-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.save-payment-method-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.payment-options {
  &__empty {
    text-align: center;
    color: #475569;
  }
}

.sense-pass {
  &__header {
    width: 100%;
    display: flex;
    align-items: center;

    &-back {
      font-size: 16px;
      padding: 0px 8px;
      cursor: pointer;

      &:hover {
        color: #4a90e2;
      }

      &-text {
        margin-left: 8px;
        font-size: 14px;
        font-weight: bold;
      }
    }
  }
  &__container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 20px;

    .form-control {
      display: flex;
      justify-content: flex-end;
      max-width: 320px;
      gap: 16px;

      label {
        text-align: center;
        margin: auto 0;
      }
    }

    .fee-text-block {
      max-width: 300px;
      margin-top: auto;
    }
  }
  &__column {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &.sensepass {
      flex: 1;
      min-width: 300px;
    }
  }

  &__saved-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #f8fafc;

    &-label {
      font-size: 0.9rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #475569;
    }

    &-value {
      font-size: 1rem;
      text-align: center;
    }
  }
}

.open-delete {
  cursor: pointer;
  width: 1.25rem;
  height: 1.25rem;
  color: #dc3545;

  &:hover {
    color: unset;
  }
}
</style>
